// src/pages/InvitationPage.js
// External invitation acceptance — multi-step wizard: Password → Payment → Terms → Success

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    CircularProgress,
    Alert,
    Checkbox,
    Stepper,
    Step,
    StepLabel,
    Link as MuiLink,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import axiosInstance from '../../../utils/axiosInstance';
import { stripe_publishable_key } from '../../../config';

const stripePromise = loadStripe(stripe_publishable_key);

export default function CustomerInvitationPage() {
    const { token } = useParams();
    const [invitationDetails, setInvitationDetails] = useState(null);
    const [step, setStep] = useState('loading'); // loading | password | payment | plan | terms | error | success
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) {
            setError('Invitation token not found in URL.');
            setStep('error');
            return;
        }
        const checkToken = async () => {
            try {
                const response = await axiosInstance.post('/invitations/check', { token });
                setInvitationDetails(response.data.data);
                setStep('password');
            } catch (err) {
                setError(err.response?.data?.message || 'This invitation link is invalid or has expired.');
                setStep('error');
            }
        };
        checkToken();
    }, [token]);

    const stripeOptions = invitationDetails?.client_secret
        ? { clientSecret: invitationDetails.client_secret, appearance: { theme: 'stripe' } }
        : undefined;

    const hasTermsStep = invitationDetails?.pending_terms && invitationDetails.pending_terms.length > 0;
    const hasPlanStep = !!invitationDetails?.marketplace_pricing;

    const stepLabels = [
        'Password',
        'Payment',
        ...(hasPlanStep ? ['Select Plan'] : []),
        ...(hasTermsStep ? ['Agreements'] : []),
    ];

    const activeStepIndex = step === 'password' ? 0
        : step === 'payment' ? 1
        : step === 'plan' ? 2
        : step === 'terms' ? (hasPlanStep ? 3 : 2)
        : step === 'success' ? stepLabels.length
        : -1;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #eef2ff 0%, #ffffff 50%, #f5f3ff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Paper
                elevation={6}
                sx={{
                    maxWidth: 480,
                    width: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                }}
            >
                {/* Branding Header */}
                {step !== 'loading' && step !== 'error' && (
                    <Box sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', px: 4, pt: 3, pb: 1, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 1, mb: 1 }}>
                            Account Setup
                        </Typography>
                    </Box>
                )}
                {step !== 'loading' && step !== 'error' && (
                    <Box sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', px: 4, py: 2 }}>
                        <Stepper
                            activeStep={activeStepIndex}
                            alternativeLabel
                            sx={{
                                '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' },
                                '& .MuiStepLabel-label.Mui-active': { color: '#fff' },
                                '& .MuiStepLabel-label.Mui-completed': { color: '#fff' },
                                '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.3)' },
                                '& .MuiStepIcon-root.Mui-active': { color: '#fff' },
                                '& .MuiStepIcon-root.Mui-completed': { color: '#fff' },
                                '& .MuiStepConnector-line': { borderColor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            {stepLabels.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                )}

                <Box sx={{ p: 4 }}>
                    {step === 'loading' && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <CircularProgress size={48} sx={{ color: '#6366f1', mb: 2 }} />
                            <Typography color="text.secondary">Validating invitation...</Typography>
                        </Box>
                    )}

                    {step === 'error' && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                            <Typography variant="h5" color="error" fontWeight={700} gutterBottom>
                                Invitation Error
                            </Typography>
                            <Typography color="text.secondary">{error}</Typography>
                        </Box>
                    )}

                    {invitationDetails && ['password', 'payment', 'plan', 'terms'].includes(step) && stripeOptions && (
                        <Elements stripe={stripePromise} options={stripeOptions}>
                            <InvitationForm
                                step={step}
                                setStep={setStep}
                                details={invitationDetails}
                                token={token}
                                hasTermsStep={hasTermsStep || false}
                                hasPlanStep={hasPlanStep || false}
                            />
                        </Elements>
                    )}

                    {step === 'success' && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                            <Typography variant="h5" color="success.main" fontWeight={700} gutterBottom>
                                Welcome Aboard!
                            </Typography>
                            <Typography color="text.secondary">
                                Setup complete! Redirecting to your dashboard...
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}


function InvitationForm({ step, setStep, details, token, hasTermsStep, hasPlanStep }) {
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();

    const [passwordChoice, setPasswordChoice] = useState(details.user_exists ? 'existing' : 'new');
    const [paymentChoice, setPaymentChoice] = useState(details.has_payment_method ? 'existing' : 'new');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentMethodId, setPaymentMethodId] = useState('');
    const [formError, setFormError] = useState(null);
    const [acceptedTermIds, setAcceptedTermIds] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState('');

    const handleFinalSetup = async (overridePaymentMethodId) => {
        setIsSubmitting(true);
        try {
            const response = await axiosInstance.post('/invitations/complete', {
                token,
                password,
                use_existing_password: passwordChoice === 'existing',
                use_existing_card: paymentChoice === 'existing',
                payment_method_id: overridePaymentMethodId ?? paymentMethodId,
                accepted_term_ids: hasTermsStep ? acceptedTermIds : [],
                ...(selectedPlan ? { selected_plan_type: selectedPlan } : {}),
            });

            const responseData = typeof response.data.data === 'string'
                ? JSON.parse(response.data.data)
                : response.data.data;

            // Store in localStorage (matching login_controller format)
            const userForStorage = {
                access_token: responseData.user.access_token,
                user_id: responseData.user.user_id,
                user_email: responseData.user.user_email,
                verified: responseData.user.verified,
                profile: responseData.user.profile,
                teams: responseData.user.teams,
                active_team: responseData.user.active_team,
            };

            localStorage.setItem('user', JSON.stringify(userForStorage));
            setStep('success');
            setTimeout(() => {
                window.location.href = "/customer-panel/dashboard";
            }, 2000);
        } catch (err) {
            setFormError(err.response?.data?.message || 'An error occurred during final setup.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePasswordStep = async () => {
        if (passwordChoice === 'existing' && !password) {
            setFormError('Please enter your existing password.');
            return;
        }
        if (passwordChoice === 'new' && (password.length < 6 || password !== confirmPassword)) {
            setFormError('Passwords must match and be at least 6 characters.');
            return;
        }

        if (passwordChoice === 'existing') {
            setIsSubmitting(true);
            try {
                await axiosInstance.post('/user/check_password', { token, password });
                setStep('payment');
                setFormError(null);
            } catch (err) {
                setFormError(err.response?.data?.message || 'Invalid password. Please try again.');
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setStep('payment');
            setFormError(null);
        }
    };

    const handlePaymentStep = async () => {
        const nextStep = hasPlanStep ? 'plan' : hasTermsStep ? 'terms' : null;

        if (paymentChoice === 'existing') {
            setPaymentMethodId(null);
            if (nextStep) {
                setStep(nextStep);
            } else {
                await handleFinalSetup();
            }
        } else {
            if (!stripe || !elements) {
                setFormError('Payment system is not ready.');
                return;
            }
            setIsSubmitting(true);

            const { error: stripeError, setupIntent } = await stripe.confirmSetup({
                elements, redirect: 'if_required'
            });

            if (stripeError) {
                setFormError(stripeError.message || 'An error occurred with your card.');
                setIsSubmitting(false);
                return;
            }

            if (setupIntent?.status === 'succeeded') {
                const newPaymentMethodId = setupIntent.payment_method;
                setPaymentMethodId(newPaymentMethodId);
                if (nextStep) {
                    setStep(nextStep);
                    setIsSubmitting(false);
                } else {
                    await handleFinalSetup(newPaymentMethodId);
                }
            } else {
                setFormError('Could not save payment method. Please try again.');
                setIsSubmitting(false);
            }
        }
    };

    const handlePlanStep = async () => {
        if (!selectedPlan) {
            setFormError('Please select a billing plan.');
            return;
        }
        if (hasTermsStep) {
            setStep('terms');
        } else {
            await handleFinalSetup();
        }
    };

    const handleTermsStep = async () => {
        if (acceptedTermIds.length !== (details.pending_terms?.length || 0)) {
            setFormError('Please accept all agreements to continue.');
            return;
        }
        await handleFinalSetup();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setFormError(null);

        if (step === 'password') await handlePasswordStep();
        if (step === 'payment') await handlePaymentStep();
        if (step === 'plan') await handlePlanStep();
        if (step === 'terms') await handleTermsStep();
    };

    const toggleTerm = (termId) => {
        setAcceptedTermIds((prev) =>
            prev.includes(termId) ? prev.filter((id) => id !== termId) : [...prev, termId]
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            {/* ===================== PASSWORD STEP ===================== */}
            {step === 'password' && (
                <Box>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <LockOutlinedIcon sx={{ fontSize: 40, color: '#6366f1', mb: 1 }} />
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            Welcome {details.fname}!
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                            {details.user_exists
                                ? "Great to see you again! Let's verify your identity."
                                : "Let's get you set up with a secure password."}
                        </Typography>
                    </Box>

                    {details.user_exists && (
                        <RadioGroup
                            value={passwordChoice}
                            onChange={(e) => setPasswordChoice(e.target.value)}
                            sx={{ mb: 2 }}
                        >
                            <FormControlLabel value="existing" control={<Radio />} label="Use existing password" />
                            <FormControlLabel value="new" control={<Radio />} label="Create new password" />
                        </RadioGroup>
                    )}

                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

                    <TextField
                        fullWidth
                        type="password"
                        label={passwordChoice === 'existing' ? 'Your existing password' : 'Create a password'}
                        placeholder={passwordChoice === 'existing' ? 'Enter your existing password' : 'Create a secure password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        sx={{ mb: 2 }}
                    />

                    {passwordChoice === 'new' && (
                        <TextField
                            fullWidth
                            type="password"
                            label="Confirm password"
                            placeholder="Confirm your new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            sx={{ mb: 2 }}
                        />
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                            mt: 1, py: 1.5, borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Continue to Payment'}
                    </Button>
                </Box>
            )}

            {/* ===================== PAYMENT STEP ===================== */}
            {step === 'payment' && (
                <Box>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => { setStep('password'); setFormError(null); }}
                        sx={{ mb: 2, color: '#6366f1' }}
                    >
                        Back
                    </Button>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <CreditCardIcon sx={{ fontSize: 40, color: '#6366f1', mb: 1 }} />
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            Save Payment Method
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                            {details.has_payment_method
                                ? 'Choose your preferred payment method'
                                : 'We must save your card on file for future use.'}
                        </Typography>
                    </Box>

                    {details.has_payment_method && (
                        <RadioGroup
                            value={paymentChoice}
                            onChange={(e) => setPaymentChoice(e.target.value)}
                            sx={{ mb: 2 }}
                        >
                            <FormControlLabel
                                value="existing"
                                control={<Radio />}
                                label={`Use saved card (${details.saved_card?.brand?.toUpperCase()} ****${details.saved_card?.last4})`}
                            />
                            <FormControlLabel value="new" control={<Radio />} label="Add new card" />
                        </RadioGroup>
                    )}

                    <Alert severity="info" sx={{ mb: 2 }}>
                        We must save your card for future use. You will not be charged now.
                    </Alert>

                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

                    {paymentChoice === 'new' && (
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, mb: 2 }}>
                            <PaymentElement options={{ layout: 'tabs' }} />
                        </Box>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isSubmitting || (paymentChoice === 'new' && !stripe)}
                        sx={{
                            mt: 1, py: 1.5, borderRadius: 2,
                            background: 'linear-gradient(135deg, #059669, #10b981)',
                            '&:hover': { background: 'linear-gradient(135deg, #047857, #059669)' },
                        }}
                    >
                        {isSubmitting
                            ? <CircularProgress size={24} color="inherit" />
                            : hasPlanStep ? 'Continue to Plan Selection'
                            : hasTermsStep ? 'Continue to Agreements' : 'Complete Setup'
                        }
                    </Button>
                </Box>
            )}

            {/* ===================== PLAN STEP ===================== */}
            {step === 'plan' && (
                <Box>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => { setStep('payment'); setFormError(null); }}
                        sx={{ mb: 2, color: '#6366f1' }}
                    >
                        Back
                    </Button>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <LocalOfferOutlinedIcon sx={{ fontSize: 40, color: '#6366f1', mb: 1 }} />
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            Select Your Plan
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                            Choose the billing plan that works best for you.
                        </Typography>
                    </Box>

                    {details.marketplace_pricing?.signup_description && (
                        <Box
                            sx={{ mb: 3, p: 2, bgcolor: '#f5f3ff', borderRadius: 2, fontSize: '0.875rem' }}
                            dangerouslySetInnerHTML={{ __html: details.marketplace_pricing.signup_description }}
                        />
                    )}

                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

                    <RadioGroup
                        value={selectedPlan}
                        onChange={(e) => setSelectedPlan(e.target.value)}
                    >
                        {details.marketplace_pricing?.price_per_hour > 0 && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2, mb: 1.5, cursor: 'pointer',
                                    border: selectedPlan === 'per_hour' ? '2px solid #6366f1' : '1px solid #e0e0e0',
                                    bgcolor: selectedPlan === 'per_hour' ? '#f5f3ff' : 'transparent',
                                }}
                                onClick={() => setSelectedPlan('per_hour')}
                            >
                                <FormControlLabel
                                    value="per_hour"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography fontWeight={600}>Per Hour</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                ${details.marketplace_pricing.price_per_hour}/hour — Pay based on agent talk time
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Paper>
                        )}
                        {details.marketplace_pricing?.price_per_instance > 0 && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2, mb: 1.5, cursor: 'pointer',
                                    border: selectedPlan === 'per_instance' ? '2px solid #6366f1' : '1px solid #e0e0e0',
                                    bgcolor: selectedPlan === 'per_instance' ? '#f5f3ff' : 'transparent',
                                }}
                                onClick={() => setSelectedPlan('per_instance')}
                            >
                                <FormControlLabel
                                    value="per_instance"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography fontWeight={600}>Per Session</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                ${details.marketplace_pricing.price_per_instance}/session — Flat rate per agent connection
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Paper>
                        )}
                        {details.marketplace_pricing?.price_per_month > 0 && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2, mb: 1.5, cursor: 'pointer',
                                    border: selectedPlan === 'per_month' ? '2px solid #6366f1' : '1px solid #e0e0e0',
                                    bgcolor: selectedPlan === 'per_month' ? '#f5f3ff' : 'transparent',
                                }}
                                onClick={() => setSelectedPlan('per_month')}
                            >
                                <FormControlLabel
                                    value="per_month"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography fontWeight={600}>Monthly</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                ${details.marketplace_pricing.price_per_month}/month — Unlimited sessions
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Paper>
                        )}
                    </RadioGroup>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isSubmitting || !selectedPlan}
                        sx={{
                            mt: 1, py: 1.5, borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
                        }}
                    >
                        {isSubmitting
                            ? <CircularProgress size={24} color="inherit" />
                            : hasTermsStep ? 'Continue to Agreements' : 'Complete Setup'
                        }
                    </Button>
                </Box>
            )}

            {/* ===================== TERMS STEP ===================== */}
            {step === 'terms' && (
                <Box>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => { setStep(hasPlanStep ? 'plan' : 'payment'); setFormError(null); }}
                        sx={{ mb: 2, color: '#6366f1' }}
                    >
                        Back
                    </Button>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            Required Agreements
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                            Please review and accept the following to complete your setup.
                        </Typography>
                    </Box>

                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

                    <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                        {details.pending_terms?.map((term) => (
                            <Paper key={term.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                                <Typography fontWeight={600}>{term.title}</Typography>
                                {term.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                        {term.description}
                                    </Typography>
                                )}
                                {term.file_url && (
                                    <MuiLink href={term.file_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', mt: 0.5, fontSize: '0.875rem' }}>
                                        View Document
                                    </MuiLink>
                                )}
                                <FormControlLabel
                                    sx={{ mt: 1 }}
                                    control={
                                        <Checkbox
                                            checked={acceptedTermIds.includes(term.id)}
                                            onChange={() => toggleTerm(term.id)}
                                        />
                                    }
                                    label="I Accept"
                                />
                            </Paper>
                        ))}
                    </Box>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isSubmitting || acceptedTermIds.length !== (details.pending_terms?.length || 0)}
                        sx={{
                            py: 1.5, borderRadius: 2,
                            background: 'linear-gradient(135deg, #059669, #10b981)',
                            '&:hover': { background: 'linear-gradient(135deg, #047857, #059669)' },
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Accept & Complete Setup'}
                    </Button>
                </Box>
            )}
        </form>
    );
}
